import React, { useEffect, useState } from 'react';
import { axiosKK } from '../../../services/networkRequest';
import { VehicleDTO } from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import {
  AddButton,
  AdminNotice,
  DataTable,
  DataTableColumn,
  InlineEditor,
  InlineEditorField,
} from '../../../components/admin/Shared';
import { DEFAULT_VEHICLES, DefaultVehicle } from './defaults';

interface Props {
  partnerId: number;
  /**
   * When true, hide the AddButton + per-row Edit/Deactivate affordances and
   * the suggested-defaults panel. The DataTable still renders so the user
   * can see what's configured.
   * Default: false (admin behaviour).
   */
  readOnly?: boolean;
}

interface VehicleFormValues extends Record<string, unknown> {
  name: string;
  vehicleClass: string;
  boxCapacity: string | number;
  weightCapacityKg: string | number;
  isActive: boolean;
}

export default function VehiclesTab({ partnerId, readOnly = false }: Props) {
  const [vehicles, setVehicles] = useState<VehicleDTO[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState<string>();

  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [editingSeed, setEditingSeed] = useState<Partial<VehicleFormValues>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saveBanner, setSaveBanner] = useState<string>();

  const refresh = React.useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosKK.get<{ vehicles: VehicleDTO[] }>(`/admin/partners/${partnerId}/vehicles`),
      axiosKK.get<{ classes: string[] }>(`/admin/reference/vehicle-classes`),
    ])
      .then(([v, c]) => {
        setVehicles(v.data.vehicles || []);
        setClasses(c.data.classes || []);
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setLoading(false));
  }, [partnerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditingId('new');
    setEditingSeed({
      name: '',
      vehicleClass: classes[0] || '',
      boxCapacity: '',
      weightCapacityKg: '',
      isActive: true,
    });
  };

  const openEdit = (v: VehicleDTO) => {
    setEditingId(v.id);
    setEditingSeed({
      name: v.name || '',
      vehicleClass: v.vehicleClass,
      boxCapacity: v.boxCapacity != null ? v.boxCapacity : '',
      weightCapacityKg: v.weightCapacityKg != null ? v.weightCapacityKg : '',
      isActive: v.isActive,
    });
  };

  const cancel = () => {
    setEditingId(null);
  };

  const submit = (values: VehicleFormValues) => {
    if (!String(values.name ?? '').trim()) {
      setNetError('Name is required.');
      return;
    }
    if (!values.vehicleClass) {
      setNetError('Vehicle class is required.');
      return;
    }
    setSubmitting(true);
    setNetError(undefined);
    setSaveBanner(undefined);

    const payload: any = {
      name: String(values.name).trim(),
      vehicleClass: values.vehicleClass,
      boxCapacity:
        values.boxCapacity === '' || values.boxCapacity == null
          ? null
          : Number(values.boxCapacity),
      weightCapacityKg:
        values.weightCapacityKg === '' || values.weightCapacityKg == null
          ? null
          : Number(values.weightCapacityKg),
    };
    if (editingId !== 'new') {
      payload.isActive = Boolean(values.isActive);
    }

    const request =
      editingId === 'new'
        ? axiosKK.post(`/admin/partners/${partnerId}/vehicles`, payload)
        : axiosKK.put(`/admin/partners/${partnerId}/vehicles/${editingId}`, payload);

    request
      .then(() => {
        const wasNew = editingId === 'new';
        setEditingId(null);
        setSaveBanner(wasNew ? 'Vehicle added.' : 'Vehicle updated.');
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const remove = (id: number) => {
    setNetError(undefined);
    setSaveBanner(undefined);
    axiosKK
      .delete(`/admin/partners/${partnerId}/vehicles/${id}`)
      .then(() => {
        setSaveBanner('Vehicle deactivated.');
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err));
  };

  const editorFields: InlineEditorField[] = [
    {
      name: 'name',
      label: 'Name / registration',
      type: 'text',
      required: true,
      placeholder: 'e.g. AB12 CDE or Big White Van',
    },
    {
      name: 'vehicleClass',
      label: 'Class',
      type: 'select',
      required: true,
      options: classes.map((c) => ({ value: c, label: c })),
    },
    {
      name: 'boxCapacity',
      label: 'Capacity (boxes)',
      type: 'number',
    },
    {
      name: 'weightCapacityKg',
      label: 'Weight capacity (kg)',
      type: 'number',
    },
    ...(editingId !== 'new'
      ? [{ name: 'isActive', label: 'Active', type: 'checkbox' as const }]
      : []),
  ];

  const addDefault = (d: DefaultVehicle) => {
    setNetError(undefined);
    setSaveBanner(undefined);
    axiosKK
      .post(`/admin/partners/${partnerId}/vehicles`, {
        name: d.name,
        vehicleClass: d.vehicleClass,
        boxCapacity: d.boxCapacity,
        weightCapacityKg: d.weightCapacityKg,
      })
      .then(() => {
        setSaveBanner(`Added ${d.name}.`);
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err));
  };

  const addAllDefaults = () => {
    setNetError(undefined);
    setSaveBanner(undefined);
    setSubmitting(true);
    Promise.all(
      DEFAULT_VEHICLES.map((d) =>
        axiosKK.post(`/admin/partners/${partnerId}/vehicles`, {
          name: d.name,
          vehicleClass: d.vehicleClass,
          boxCapacity: d.boxCapacity,
          weightCapacityKg: d.weightCapacityKg,
        })
      )
    )
      .then(() => {
        setSaveBanner(`Added ${DEFAULT_VEHICLES.length} suggested vehicles.`);
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const presentClasses = new Set(vehicles.map((v) => v.vehicleClass));
  const suggestedDefaults = DEFAULT_VEHICLES.filter((d) => !presentClasses.has(d.vehicleClass));
  const showDefaultsPanel =
    !readOnly && !loading && vehicles.length === 0 && editingId === null;

  return (
    <section className="admin-partner-vehicles">
      <AdminNotice intent="error">{netError}</AdminNotice>
      <AdminNotice intent="success">{saveBanner}</AdminNotice>

      {loading ? (
        <p>Loading vehicles…</p>
      ) : (
        <>
          {showDefaultsPanel && suggestedDefaults.length > 0 && (
            <div className="partner-defaults">
              <h4>Suggested UK 2026 fleet</h4>
              <p className="partner-defaults__hint">
                Four sensible van sizes for UK student-storage operations. Capacities are conservative —
                edit any row after adding to match the actual fleet.
              </p>
              <div className="partner-defaults__buttons">
                {suggestedDefaults.map((d) => (
                  <button
                    key={d.vehicleClass}
                    type="button"
                    className="partner-defaults__button"
                    onClick={() => addDefault(d)}
                    title={d.description}
                  >
                    + {d.name} <span className="partner-defaults__hint">({d.boxCapacity} boxes / {d.weightCapacityKg} kg)</span>
                  </button>
                ))}
              </div>
              <AddButton onClick={addAllDefaults} disabled={submitting}>
                Add all {suggestedDefaults.length} suggested vehicles
              </AddButton>
            </div>
          )}


          {!readOnly && editingId !== null && (
            <InlineEditor<VehicleFormValues>
              title={editingId === 'new' ? 'New vehicle' : `Edit vehicle #${editingId}`}
              fields={editorFields}
              initialValues={editingSeed}
              isSubmitting={submitting}
              onSubmit={submit}
              onCancel={cancel}
            />
          )}

          <DataTable<VehicleDTO>
            columns={[
              { key: 'id', label: 'ID' },
              { key: 'name', label: 'Name', render: (v) => v.name || '—' },
              { key: 'vehicleClass', label: 'Class' },
              { key: 'boxCapacity', label: 'Boxes', render: (v) => v.boxCapacity ?? '—' },
              { key: 'weightCapacityKg', label: 'Weight (kg)', render: (v) => v.weightCapacityKg ?? '—' },
              { key: 'isActive', label: 'Active', render: (v) => (v.isActive ? 'Yes' : 'No') },
            ] as DataTableColumn<VehicleDTO>[]}
            rows={vehicles}
            keyOf={(v) => v.id}
            emptyMessage="No vehicles configured."
            aboveTable={
              readOnly ? undefined : (
                <AddButton onClick={openCreate} disabled={editingId !== null}>
                  Add vehicle
                </AddButton>
              )
            }
            actionsColumn={
              readOnly
                ? undefined
                : {
                    render: (v) => (
                      <>
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => openEdit(v)}
                          disabled={editingId !== null}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => remove(v.id)}
                        >
                          Deactivate
                        </button>
                      </>
                    ),
                  }
            }
          />
        </>
      )}
    </section>
  );
}
